import './style.css'
import {webConstants} from "../../webConstants";
import {SubmitHandler, useForm} from "react-hook-form";
import {useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {isAxiosError} from "axios";
import {login as loginRequest} from "../../services/authService";

type FormValues = {
    login: string
    password: string
}

export function LoginPage() {
    const [showPassword, setShowPassword] = useState<boolean>(false)
    const [serverError, setServerError] = useState<string | null>(null)
    const navigate = useNavigate()
    const location = useLocation()
    const redirectPath =
        (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/dashboard'

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<FormValues>()

    const onSubmit: SubmitHandler<FormValues> = async (data) => {
        setServerError(null)
        try {
            await loginRequest(data)
            navigate(redirectPath, { replace: true })
        } catch (error) {
            if (isAxiosError(error) && error.response?.status === 401) {
                setServerError('Incorrect login or password')
                return
            }
            setServerError('Failed to sign you in. Please try again later.')
        }
    }

    return (
        <div>
            <div className="ambient-light-bg">
                <div className="light-orb orb-1"></div>
                <div className="light-orb orb-2"></div>
                <div className="light-orb orb-3"></div>
            </div>
            <div className="relative min-h-screen flex flex-col justify-center items-center px-4 sm:px-6">
                <div className="w-full max-w-[420px] animate-[fadeIn_0.5s_ease-out]">
                    <div className="glass-panel rounded-3xl p-1 overflow-hidden">
                        <div className="bg-black/20 rounded-[20px] p-8 flex flex-col gap-8">
                            <div className="flex flex-col items-center justify-center text-center gap-4">
                                <div
                                    className="flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-blue-700 text-white shadow-lg shadow-primary/20 mb-2">
                                    <span className="material-symbols-outlined text-3xl">token</span>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <h1 className="font-bold text-2xl tracking-tight uppercase">{webConstants.projectName}</h1>
                                    <p className="text-sm text-slate-400">Enter your credentials to access the
                                        dashboard</p>
                                </div>
                            </div>
                            <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
                                <div className="space-y-4">
                                    <div className="space-y-1.5">
                                        <label
                                            className="text-xs font-medium text-slate-400 ml-1 uppercase tracking-wider"
                                            htmlFor="username">Username</label>
                                        <div className="relative group">
                                            <div
                                                className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-primary text-slate-500">
                                                <span className="material-symbols-outlined text-[20px]">person</span>
                                            </div>
                                            <input autoComplete="username"
                                                   className={`glass-input w-full rounded-xl py-3.5 pl-11 pr-4 text-sm placeholder:text-slate-600 focus:ring-0 ${errors.login ? 'border-red-500' : ''}`}
                                                   id="username"
                                                   placeholder="Enter your username" type="text" {...register("login", {required: "Login required"})}/>
                                        </div>
                                        {errors?.login?.message && <p className="text-xs text-red-400 ml-1">{errors.login.message}</p>}
                                    </div>
                                    <div className="space-y-1.5">
                                        <label
                                            className="text-xs font-medium text-slate-400 ml-1 uppercase tracking-wider"
                                            htmlFor="password">Password</label>
                                        <div className="relative group">
                                            <div
                                                className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-primary text-slate-500">
                                                <span className="material-symbols-outlined text-[20px]">lock</span>
                                            </div>
                                            <input autoComplete="current-password"
                                                   className={`glass-input w-full rounded-xl py-3.5 pl-11 pr-12 text-sm placeholder:text-slate-600 focus:ring-0 ${errors.password ? 'border-red-500' : ''}`}
                                                   id="password" placeholder="Enter your password"
                                                   type={showPassword ? 'text' : 'password'} {...register("password", {
                                                required: "Password required",
                                            })}/>
                                            <button
                                                className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-500 transition-colors focus:outline-none hover:text-slate-400"
                                                type="button" onClick={() => setShowPassword(!showPassword)}>
                                                <span
                                                    className="material-symbols-outlined text-[20px] cursor-pointer">{showPassword ? 'visibility_off' : 'visibility'}</span>
                                            </button>
                                        </div>
                                        {errors?.password?.message && <p className="text-xs text-red-400 ml-1">{errors.password.message}</p>}
                                    </div>
                                </div>
                                {serverError && (
                                    <p className="text-sm text-red-400 text-center">{serverError}</p>
                                )}
                                <button
                                    className="mt-2 relative w-full overflow-hidden group rounded-xl bg-primary px-8 py-3.5 font-semibold text-white shadow-[0_0_20px_rgba(19,91,236,0.3)] transition-all hover:shadow-[0_0_30px_rgba(19,91,236,0.5)] hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                    type="submit" disabled={isSubmitting} aria-busy={isSubmitting}>
                                    <div
                                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:animate-[shimmer_1.5s_infinite]"></div>
                                    <span className="relative flex items-center justify-center gap-2">
<span className="material-symbols-outlined text-[20px]">login</span>
                                Sign In
                            </span>
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
